import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useEditorState } from '@tiptap/react';
import { ToolbarItemType } from './types.js';
import { css, styled } from 'styled-components';
import { getDefaultRadixPortalContainer } from '@fabric-app/compat';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { mediaHover, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { useResponsive } from '@fabric-app/ui/hooks/useResponsive';
import Tooltip from '@fabric-app/ui/components/Tooltip';

const ButtonStyles = css([
  `position:relative;border-radius:6px;border:none;padding:0 0.5rem;display:flex;align-items:center;justify-content:center;height:1.75rem;min-width:1.75rem;font-size:1em;margin:0;outline:none;font-weight:500;color:var(--fabric-color-text-tertiary);background:none;`,
  `{&:hover{color:var(--fabric-color-text-primary);background:var(--fabric-color-bg-tertiary);}}&:focus{position:relative;box-shadow:0 0 0 2px rgb(var(--color-primary));}`,
  `{height:2rem;min-width:2rem;font-size:0.8em;padding:0 0.75rem;}&:disabled{opacity:0.5;cursor:not-allowed;}`
], mediaHover, mediaMobile);
const ToolbarButtonLabel = styled.span.withConfig({
  displayName: "ToolbarItems__ToolbarButtonLabel",
  componentId: "sc-8e819d1d-0"
})([
  ``
]);
const ToolbarToggleItem = styled.button.withConfig({
  displayName: "ToolbarItems__ToolbarToggleItem",
  componentId: "sc-8e819d1d-1"
})([
  ``,
  ` margin-left:2px;`,
  `{display:none;}&:first-child{margin-left:0;}&[data-state='on']{color:rgba(var(--fabric-color-app-primary-text),1);background:rgba(var(--fabric-color-app-primary-text-rgb),var(--opacity-surface-selection));}`
], ButtonStyles, ToolbarButtonLabel);
const ToolbarButtonEl = styled.button.withConfig({
  displayName: "ToolbarItems__ToolbarButtonEl",
  componentId: "sc-8e819d1d-2"
})([
  ``,
  ` `,
  `{display:none;}`
], ButtonStyles, ToolbarButtonLabel);
const DropdownContent = styled(Dropdown.Content).withConfig({
  displayName: "ToolbarItems__DropdownContent",
  componentId: "sc-8e819d1d-3"
})([
  `min-width:max-content;background-color:var(--fabric-color-bg-primary);border-radius:6px;padding:5px;box-shadow:0px 10px 38px -10px rgba(128,128,128,0.35),0px 10px 20px -15px rgba(128,128,128,0.2);animation-duration:400ms;animation-timing-function:cubic-bezier(0.16,1,0.3,1);will-change:transform,opacity;z-index:130;`,
  `,`,
  `{margin-left:0;width:100%;font-size:13px;line-height:1;border-radius:3px;display:flex;align-items:center;height:25px;padding:0 15px;position:relative;user-select:none;outline:none;gap:20px;`,
  `{display:inline-block;}}`,
  `{max-height:200px;overflow-y:auto;`,
  `,`,
  `{height:2.5rem;padding:0 1.25rem;font-size:1em;}}`
], ToolbarToggleItem, ToolbarButtonEl, ToolbarButtonLabel, mediaMobile, ToolbarToggleItem, ToolbarButtonEl);
const TriggerContent = styled.span.withConfig({
  displayName: "ToolbarItems__TriggerContent",
  componentId: "sc-8e819d1d-4"
})([
  `display:flex;align-items:center;flex-grow:1;gap:12px;`
]);
const ToolbarButtonShortcut = styled.span.withConfig({
  displayName: "ToolbarItems__ToolbarButtonShortcut",
  componentId: "sc-8e819d1d-5"
})([
  `display:flex;justify-content:flex-end;align-items:center;flex-shrink:0;`,
  `{display:none;}`
], mediaMobile);
const DropdownTrigger = styled(Dropdown.Trigger).withConfig({
  displayName: "ToolbarItems__DropdownTrigger",
  componentId: "sc-8e819d1d-6"
})([
  ``,
  ` display:flex;align-items:center;justify-content:space-between;&[data-state='partially']{&::before{content:'';width:3px;height:3px;position:absolute;left:4px;top:4px;border-radius:50%;background-color:rgba(var(--color-primary),0.8);}}`
], ButtonStyles);
const DropdownMenuArrow = styled(Dropdown.Arrow).withConfig({
  displayName: "ToolbarItems__DropdownMenuArrow",
  componentId: "sc-8e819d1d-7"
})([
  `fill:var(--fabric-color-bg-primary);`
]);
const DropdownSeparator = styled(Dropdown.DropdownMenuSeparator).withConfig({
  displayName: "ToolbarItems__DropdownSeparator",
  componentId: "sc-8e819d1d-8"
})([
  `height:1px;background-color:var(--fabric-color-border-primary);margin:5px;`
]);
const ToolbarToggleComponent = ({ editor, item: { label, icon, onClick, getIsActive, shortcut }, insideDropdown = false }) => {
  const { isActive } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isActive: getIsActive(snapshot.editor)
    })
  });
  const renderContent = () => {
    const content = /* @__PURE__ */ jsx(ToolbarToggleItem, {
      "data-state": isActive ? "on" : "off",
      onClick: () => onClick(editor),
      "aria-label": label,
      "aria-haspopup": insideDropdown,
      "aria-expanded": insideDropdown && isActive,
      type: "button",
      children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsxs(TriggerContent, {
            children: [
              icon,
              /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                children: label
              })
            ]
          }),
          shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
            children: shortcut
          })
        ]
      }) : icon
    });
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(Dropdown.DropdownMenuItem, {
        asChild: true,
        children: content
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment, {
        children: [
          label,
          shortcut
        ]
      }),
      children: content
    });
  };
  return renderContent();
};
const ToolbarButtonComponent = ({ editor, item: { label, icon, onClick, shortcut, isDisabled: isDisabledFn }, insideDropdown = false }) => {
  const { isDisabled } = useEditorState({
    editor,
    selector: (snapshot) => ({
      isDisabled: isDisabledFn?.(snapshot.editor)
    })
  });
  const renderContent = () => {
    const content = /* @__PURE__ */ jsx(ToolbarButtonEl, {
      "data-inside-dropdown": insideDropdown,
      onClick: () => onClick(editor),
      "aria-label": label,
      "aria-haspopup": insideDropdown,
      type: "button",
      disabled: isDisabled,
      children: insideDropdown ? /* @__PURE__ */ jsxs(Fragment, {
        children: [
          /* @__PURE__ */ jsxs(TriggerContent, {
            children: [
              icon,
              /* @__PURE__ */ jsx(ToolbarButtonLabel, {
                children: label
              })
            ]
          }),
          shortcut && /* @__PURE__ */ jsx(ToolbarButtonShortcut, {
            children: shortcut
          })
        ]
      }) : icon ? icon : label
    });
    if (insideDropdown) {
      return /* @__PURE__ */ jsx(Dropdown.DropdownMenuItem, {
        asChild: true,
        children: content
      });
    }
    return /* @__PURE__ */ jsx(Tooltip, {
      label: /* @__PURE__ */ jsxs(Fragment, {
        children: [
          label,
          shortcut
        ]
      }),
      children: content
    });
  };
  return renderContent();
};
const ToolbarDropdownComponent = ({ editor, item: { icon, label, items } }) => {
  const hasActiveItems = items.some((item) => item.type === ToolbarItemType.Toggle && item.getIsActive(editor));
  const { isMobile } = useResponsive();
  return /* @__PURE__ */ jsxs(Dropdown.Root, {
    modal: !isMobile(),
    children: [
      /* @__PURE__ */ jsx(Tooltip, {
        label,
        children: /* @__PURE__ */ jsx(DropdownTrigger, {
          "aria-label": label,
          "data-state": hasActiveItems ? "partially" : "off",
          type: "button",
          onTouchStart: () => editor.chain().focus(),
          children: icon
        })
      }),
      /* @__PURE__ */ jsx(Dropdown.Portal, {
        container: getDefaultRadixPortalContainer(),
        children: /* @__PURE__ */ jsxs(DropdownContent, {
          align: "start",
          sideOffset: 10,
          onCloseAutoFocus: (e) => e.preventDefault(),
          onFocusOutside: isMobile() ? (e) => e.preventDefault() : void 0,
          side: isMobile() ? "top" : "bottom",
          children: [
            renderDropdownContent(editor, items),
            /* @__PURE__ */ jsx(DropdownMenuArrow, {})
          ]
        })
      })
    ]
  });
};
const ToolbarSeparatorComponent = () => {
  return /* @__PURE__ */ jsx(DropdownSeparator, {});
};
const renderDropdownContent = (editor, items) => {
  return items.map((item) => {
    switch (item.type) {
      case ToolbarItemType.Toggle:
        return /* @__PURE__ */ jsx(ToolbarToggleComponent, {
          editor,
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Button:
        return /* @__PURE__ */ jsx(ToolbarButtonComponent, {
          editor,
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.DropdownSeparator:
        return /* @__PURE__ */ jsx(ToolbarSeparatorComponent, {
          item,
          insideDropdown: true
        }, `dropdown-item-${item.key}`);
      case ToolbarItemType.Custom:
        return item.render(`dropdown-item-${item.key}`, editor, true);
    }
  });
};

export { ToolbarButtonComponent, ToolbarDropdownComponent, ToolbarToggleComponent };
//# sourceMappingURL=ToolbarItems.js.map
